import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBridgeClubAuth } from '../../contexts/BridgeClubAuthContext';
import {
  deliveryAPI, DeliveryAvailability, SubscribeResult, TierCopy,
} from '../../services/deliveryAPI';
import WholesaleLine from './WholesaleLine';

const RED = '#8B0000';

const card: React.CSSProperties = {
  background: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  padding: '14px 16px', marginBottom: '1rem',
};
const heading: React.CSSProperties = { margin: '0 0 6px', fontSize: 17, color: RED };
const lead: React.CSSProperties = { margin: '0 0 8px', fontWeight: 600, color: '#111827' };
const para: React.CSSProperties = { margin: '0 0 10px', color: '#374151' };
const field: React.CSSProperties = {
  width: '100%', minHeight: 44, padding: '0 12px', borderRadius: 8,
  border: '1px solid #d1d5db', fontSize: 16, boxSizing: 'border-box',
};
const label: React.CSSProperties = { fontSize: 13, color: '#374151', fontWeight: 600 };
const primary: React.CSSProperties = {
  minHeight: 44, padding: '0 20px', borderRadius: 8, border: 'none', background: RED,
  color: '#fff', fontWeight: 700, fontSize: 15, cursor: 'pointer',
};
const secondary: React.CSSProperties = {
  ...primary, background: '#fff', color: RED, border: `1px solid ${RED}`,
};

/** Heading, product line and paragraphs, exactly as the server wrote them. */
const CopyBlock: React.FC<{ copy: TierCopy }> = ({ copy }) => (
  <>
    <h3 style={heading}>{copy.heading}</h3>
    <p style={lead}>{copy.product}</p>
    {copy.paragraphs.map((text, i) => <p key={i} style={para}>{text}</p>)}
  </>
);

/**
 * What a member with NO delivery subscription sees where the subscription card would be.
 *
 * This is the discovery fix. Before it, SubscriptionCard returned null for a
 * non-subscriber, so a customer living in a delivery ZIP was never told delivery existed
 * and had no way to ask for it - the only route to a subscription was a manager
 * converting a waitlist lead.
 *
 * Three wordings, decided server-side off the member's own ZIP, and WRITTEN server-side
 * too (delivery/opening.py) so this card and the registration flow never drift apart.
 * The ZIP lists decide wording only - every member is captured in StoreOpeningInterest
 * whatever their ZIP.
 */
const DeliveryOfferCard: React.FC = () => {
  const { member, refreshMember } = useBridgeClubAuth();
  const navigate = useNavigate();
  const [offer, setOffer] = useState<DeliveryAvailability | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<SubscribeResult | null>(null);
  const [optedIn, setOptedIn] = useState(false);
  const [optInMessage, setOptInMessage] = useState<string | null>(null);
  // The street address the registration flow already collected, kept as a lead. Confirm
  // rather than type it twice.
  const [pending, setPending] = useState<DeliveryAvailability['pending_address']>(null);
  const [form, setForm] = useState({ service_address: '', city: '', state: 'ID', postal_code: '' });

  // LANDING ATTRIBUTION (Brad, 2026-09-25). The landing page and the app share an
  // origin, so the session id the page kept in localStorage is still here after
  // registration. Hand it back once, then forget it; the tracker turns that visit into
  // a signup. Never in the member's way: a failure just leaves the key for next time.
  useEffect(() => {
    let sid = '';
    try { sid = localStorage.getItem('rbf_landing_sid') || ''; } catch { sid = ''; }
    if (!sid) return;
    deliveryAPI.attributeLanding(sid)
      .then(() => { try { localStorage.removeItem('rbf_landing_sid'); } catch { /* fine */ } })
      .catch(() => { /* try again next visit */ });
  }, []);

  useEffect(() => {
    let alive = true;
    deliveryAPI.getAvailability()
      .then((a) => {
        if (!alive) return;
        setOffer(a);
        setOptedIn(!!a.opted_in);
        setPending(a.pending_address ?? null);
        if (a.pending_address) {
          setForm({
            service_address: a.pending_address.service_address,
            city: a.pending_address.city,
            state: a.pending_address.state || 'ID',
            postal_code: a.pending_address.postal_code,
          });
        } else if (a.prefill) {
          setForm((f) => ({
            ...f,
            city: a.prefill!.city,
            state: a.prefill!.state || 'ID',
            postal_code: a.prefill!.postal_code,
          }));
        }
      })
      .catch(() => { if (alive) setOffer(null); });
    return () => { alive = false; };
  }, [member?.id]);

  const submit = async (event?: React.FormEvent) => {
    event?.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await deliveryAPI.subscribe(form);
      if (result.subscribed) {
        // The subscriber row now exists, so the member payload flips and the existing
        // setup flow resumes at the delivery steps. Nothing new after this point.
        await refreshMember();
        navigate('/bridge-club/onboarding');
        return;
      }
      setOutcome(result);      // out of area, and kept on the list
      setOpen(false);
    } catch (err: any) {
      setError(err?.error || err?.message || err?.detail || "That didn't save. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const optIn = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await deliveryAPI.optInToOpeningAlerts();
      setOptedIn(true);
      setOptInMessage(result.message);
    } catch (err: any) {
      setError(err?.error || "That didn't save. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (!offer || !offer.copy) return null;
  const copy = offer.copy;
  // The service switch (delivery/pause.py): signing up stays open while delivery is
  // paused - nobody is charged before activation - but the flow says so in one line.
  const pauseNote = offer.service_pause_line ? (
    <p style={{ ...para, color: '#991B1B', fontWeight: 600 }}>{offer.service_pause_line}</p>
  ) : null;

  const errorBox = error && (
    <div style={{ background: '#FEF2F2', color: '#991B1B', borderRadius: 8, padding: '8px 10px', marginBottom: 10, fontSize: 14 }}>
      {error}
    </div>
  );

  // Asked for delivery from outside the service area: kept on the list, told plainly.
  if (outcome && !outcome.subscribed) {
    return (
      <div style={card}>
        <h3 style={heading}>Home delivery</h3>
        <p style={lead}>{copy.product}</p>
        <p style={{ margin: '0 0 6px' }}>{outcome.message}</p>
        {outcome.opening_message && (
          <p style={{ margin: 0, fontSize: 14, color: '#374151' }}>{outcome.opening_message}</p>
        )}
        <WholesaleLine wholesale={copy.wholesale} />
      </div>
    );
  }

  if (offer.status === 'opening') {
    return (
      <div style={card}>
        <CopyBlock copy={copy} />
        {pauseNote}
        <WholesaleLine wholesale={copy.wholesale} />
      </div>
    );
  }

  if (offer.status === 'growing') {
    return (
      <div style={card}>
        <CopyBlock copy={copy} />
        {errorBox}
        {optedIn ? (
          <p style={{ margin: 0, color: '#374151' }}>
            {optInMessage || "You're on the list. We'll let you know when we reach your area."}
          </p>
        ) : (
          <>
            {offer.ask && <p style={para}>{offer.ask}</p>}
            <button style={{ ...secondary, opacity: busy ? 0.7 : 1 }} disabled={busy} onClick={optIn}>
              {busy ? 'Saving…' : 'Tell me when you reach my area'}
            </button>
          </>
        )}
        <WholesaleLine wholesale={copy.wholesale} />
      </div>
    );
  }

  if (offer.status !== 'available') return null;

  return (
    <div style={card}>
      <CopyBlock copy={copy} />
      {pauseNote}

      {errorBox}

      {pending && !open ? (
        <>
          <p style={{ margin: '0 0 10px' }}>
            We have your address as <strong>{pending.service_address}</strong>
            {pending.city ? `, ${pending.city}` : ''}. Nothing is charged until your first
            delivery.
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button style={{ ...primary, opacity: busy ? 0.7 : 1 }} disabled={busy}
              onClick={() => submit()}>
              {busy ? 'Setting up…' : 'Confirm and set up delivery'}
            </button>
            <button style={secondary} disabled={busy} onClick={() => setOpen(true)}>
              Use a different address
            </button>
          </div>
        </>
      ) : !open ? (
        <button style={primary} onClick={() => setOpen(true)}>Set up home delivery</button>
      ) : (
        <form onSubmit={submit}>
          <p style={{ margin: '0 0 10px', fontSize: 14, color: '#374151' }}>
            Where should the driver come? We need the street address, not just the town.
          </p>
          <div style={{ marginBottom: 10 }}>
            <label style={label} htmlFor="delivery-street">Street address</label>
            <input id="delivery-street" style={field} value={form.service_address} autoFocus
              placeholder="1234 Farm Rd"
              onChange={(e) => setForm({ ...form, service_address: e.target.value })} />
          </div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 140px' }}>
              <label style={label} htmlFor="delivery-city">City</label>
              <input id="delivery-city" style={field} value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })} />
            </div>
            <div style={{ flex: '0 0 72px' }}>
              <label style={label} htmlFor="delivery-state">State</label>
              <input id="delivery-state" style={field} value={form.state} maxLength={2}
                onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })} />
            </div>
            <div style={{ flex: '0 0 104px' }}>
              <label style={label} htmlFor="delivery-zip">ZIP</label>
              <input id="delivery-zip" style={field} value={form.postal_code} inputMode="numeric"
                onChange={(e) => setForm({ ...form, postal_code: e.target.value })} />
            </div>
          </div>
          <button type="submit" style={{ ...primary, opacity: busy ? 0.7 : 1 }} disabled={busy}>
            {busy ? 'Setting up…' : 'Continue'}
          </button>
        </form>
      )}

      <WholesaleLine wholesale={copy.wholesale} />
    </div>
  );
};

export default DeliveryOfferCard;
